import type { Faction } from '../../core/constants.ts';
import type { AnimState, AssetRole } from './roles.ts';

/**
 * How a model is fitted to the space it is placed in.
 *
 * - `none`   — the manifest's own scale, and nothing else. Right for characters
 *              and props, which have a true size of their own.
 * - `fit`    — uniform scale so the whole model fits inside the target extents.
 * - `fitXZ`  — uniform on the footprint, independent on height. Right for
 *              pillars, whose profile should not thin out as they get taller.
 * - `stretch`— per-axis, exactly filling the target. Fills the box and distorts
 *              the model doing it; a last resort.
 */
export type FitMode = 'none' | 'fit' | 'fitXZ' | 'stretch';

/**
 * Per-role material corrections, applied once at load.
 *
 * This exists to answer a specific question cheaply: is a pack too bright and
 * cheerful because of its geometry, or because of its albedo and the lighting it
 * was authored for? A clean low-poly model desaturated, darkened and lit
 * dramatically reads nothing like the same model under a neutral studio light,
 * and finding that out should not cost a re-modelling budget.
 *
 * Applied to the shared scene rather than to each instance, so it costs nothing
 * per mage. Faction tint is applied afterwards, per instance, and overrides the
 * base colour where both are set.
 */
export interface MaterialOverrides {
  /** 0 leaves the hue alone, 1 takes it to grey. */
  desaturate?: number;
  /** Multiplies value. 0.6 is a good starting point for a dark grade. */
  darken?: number;
  /** Replaces the base colour outright. Blunt, but decisive. */
  color?: number;
  roughness?: number;
  metalness?: number;
  emissive?: number;
  emissiveIntensity?: number;
  /** Faceted rather than smoothed, which reads as carved rather than moulded. */
  flatShading?: boolean;
  /** Kills a pack's baked-in lighting, which is what usually fights your own. */
  dropMaps?: boolean;
}

export interface AssetEntry {
  /** Served from `public/`, so swapping a pack is replacing a directory. */
  url: string;
  /** Applied before fitting. Packs disagree wildly about what one unit means. */
  scale?: number;
  /** Radians, applied before fitting. For packs authored on a different axis. */
  rotation?: { x?: number; y?: number; z?: number };
  /** Nudge along Y after anchoring, for models whose origin is not at the feet. */
  yOffset?: number;
  fit?: FitMode;
  /**
   * This pack's clip names against the states the game asks for. The single
   * most pack-specific thing there is: Quaternius, Mixamo and Synty name and rig
   * differently, and without this the names leak into the renderer.
   */
  clips?: Partial<Record<AnimState, string>>;
  /**
   * A separate file to take the clips from.
   *
   * Three.js binds animation tracks to bones by *name*, so one set of clips
   * drives every character sharing a skeleton. Baking the same forty-odd
   * animations into four mages would ship the same megabytes four times; loaded
   * once from here, they cost that once. The registry caches by url.
   */
  animationsFrom?: string;
  /**
   * How fast each locomotion clip was authored to move, metres per second.
   *
   * Every clip in a pack is animated in place: the skeleton never leaves the
   * origin, and the feet slide backwards under a stationary body. That works
   * only if the clip is played at the rate its author had in mind — a jog cycle
   * drawn for someone covering two and a half metres a second, played straight
   * while the game moves them at four, puts the feet somewhere the ground is
   * not. Which is what foot sliding *is*, and why it reads as weightless.
   *
   * The number is not in the file. It has to be recovered from the clip, by
   * measuring how far a foot travels front to back across a gait cycle — that
   * distance is one step, two steps make a cycle, and the rest is division.
   * `tools/measure_stride.py` does it and prints the table.
   *
   * Pack-specific by nature, which is why it lives here with the clip names
   * rather than in the animator. A pack with root motion would not need it at
   * all; a pack authored at half speed would need every number halved.
   */
  clipSpeeds?: Partial<Record<AnimState, number>>;
  /**
   * Materials whose name begins with this take the faction colour. Everything
   * else — skin, hair, eyes — is left alone.
   *
   * A prefix rather than an exact name because Blender appends `.001` and `.002`
   * when the same material is used on several surfaces, so one robe becomes
   * three materials with three names by the time it reaches a file.
   */
  tintMaterial?: string;
  /**
   * How much the faction material lights itself, 0..1.
   *
   * A tint alone multiplies against the texture, and a dark texture in a dark
   * room stays dark — which is how a Balance mage and an Order mage both ended
   * up as the same murky shape at twenty metres. A little emissive means the
   * cloth carries its colour rather than waiting to be lit, and it survives the
   * fog. This is the same trick that keeps Valorant's characters readable
   * against its environments: the world is muted and the people are not.
   */
  tintEmissive?: number;
  /**
   * Drop the faction material's base texture entirely, leaving flat colour.
   *
   * The blunt instrument, for a pack whose cloth texture is so dark that even an
   * emissive cannot rescue it. Loses the fabric detail; gains a silhouette you
   * can name across a room.
   */
  tintFlat?: boolean;
  /**
   * Whole textures swapped by faction, keyed by material name prefix.
   *
   * ```ts
   * factionTextures: {
   *   MI_Wizard: {
   *     order:   '/models/mages/tex/wizard-blue.jpg',
   *     chaos:   '/models/mages/tex/wizard-red.jpg',
   *     balance: '/models/mages/tex/wizard-green.jpg',
   *   },
   * }
   * ```
   *
   * The alternative to tinting one garment, and much the stronger of the two:
   * an artist decides what red means on that costume rather than a multiply
   * deciding for them, and the whole outfit reads rather than one panel of it.
   *
   * It costs nothing in geometry. Three factions share one model, one skeleton
   * and one set of clips — only the image differs, so this is four models and
   * twelve textures, never twelve models. Textures are cached by url and only
   * the factions actually in the match are fetched.
   */
  factionTextures?: Record<string, Partial<Record<Faction, string>>>;
  /** See MaterialOverrides. The cheapest way to test a pack against a grade. */
  materials?: MaterialOverrides;
  /**
   * Where the model's origin sits once it is placed.
   *
   * `base` is right for everything that stands on a floor, which until now was
   * everything: a mage placed at its feet, a torch at its bracket. A projectile
   * is not standing on anything — it spins about its own centre and is drawn at
   * the point the simulation says it is — so anchoring it at its base hangs it
   * below itself and turns it about its top edge.
   */
  anchor?: 'base' | 'centre';
}

/**
 * The art manifest.
 *
 * Empty on purpose. Every role falls back to its gray-box primitive, so the game
 * runs exactly as it did — and a role can be adopted one at a time by adding a
 * line here. Nothing else in the renderer changes when it is.
 *
 * A worked example, for whenever a pack is chosen:
 *
 * ```ts
 * 'prop.torch': {
 *   url: '/models/props/torch.glb',
 *   scale: 1,
 *   fit: 'none',
 * },
 * 'mage.cleric': {
 *   url: '/models/mages/cleric.glb',
 *   fit: 'none',
 *   clips: { idle: 'Idle', walk: 'Walk', run: 'Run', cast: 'Spellcast', die: 'Death' },
 *   // The dark grade, for testing whether a bright pack survives one.
 *   materials: { desaturate: 0.55, darken: 0.6, roughness: 0.85, flatShading: true },
 * },
 * ```
 */
/**
 * The clip names, shared by every class because every class shares the pack.
 *
 * Written once: four copies of this table is four chances for one of them to
 * drift, and a drifted name is a mage silently stuck in bind pose.
 */
const MAGE_CLIPS: Partial<Record<AnimState, string>> = {
  idle: 'Idle_Torch_Loop',
  walk: 'Walk_Loop',
  jog: 'Jog_Fwd_Loop',
  run: 'Sprint_Loop',
  crouchIdle: 'Crouch_Idle_Loop',
  crouchWalk: 'Crouch_Fwd_Loop',
  jump: 'Jump_Loop',
  castStart: 'Spell_Simple_Enter',
  castHold: 'Spell_Simple_Idle_Loop',
  castRelease: 'Spell_Simple_Shoot',
  castEnd: 'Spell_Simple_Exit',
  hit: 'Hit_Chest',
  die: 'Death01',
};

/**
 * What each of those clips was drawn for, metres per second.
 *
 * Measured, not guessed — see `tools/measure_stride.py`, which runs the skeleton
 * forward and watches a foot. The numbers are worth reading next to the game's
 * own speeds, because the gap is the whole reason this table exists:
 *
 * | clip        | authored for | game moves at        |
 * |-------------|--------------|----------------------|
 * | Walk_Loop   | 0.95 m/s     | — (fallback only)    |
 * | Jog_Fwd     | 2.50 m/s     | 4.2 (`WALK_SPEED`)   |
 * | Sprint      | 3.29 m/s     | 7.0 (`RUN_SPEED`)    |
 * | Crouch_Fwd  | 0.78 m/s     | 1.9 (`CROUCH_SPEED`) |
 *
 * So every locomotion clip in the game has been playing at somewhere between a
 * half and a third of the rate the ground was moving underneath it. That is not
 * a subtle error, and it is most of why the running looked wrong.
 *
 * Note what the table does *not* say. The animator corrects the ratio but will
 * not correct it past `MAX_STRIDE_SCALE`, so a sprint at 7 m/s is still asking
 * for more than the clip can honestly give. The remaining slide is small, and
 * the real fix for it is a slower `RUN_SPEED` or a faster clip — a design
 * decision, not something to bury in a scale factor.
 */
const MAGE_CLIP_SPEEDS: Partial<Record<AnimState, number>> = {
  walk: 0.95,
  jog: 2.50,
  run: 3.29,
  crouchWalk: 0.78,
};

export const ASSET_MANIFEST: Partial<Record<AssetRole, AssetEntry>> = {
  /*
   * Library shelves and storage crates — scenery that blocks.
   *
   * `fit: 'none'`, so both keep the size they were modelled at. Stretching a
   * 1.46m bookshelf across a 3.3m run of shelving would read as one enormous
   * cabinet, so the renderer repeats it along the box instead; see `dressBox` in
   * world.ts. The box in `LevelGeometry` remains authoritative for collision and
   * line of sight either way — this only decides what is drawn inside it.
   */
  /*
   * The wall bracket. `fit: 'none'` — it is a real object at a real size, and
   * stretching it into a notional box would make a torch the size of the wall
   * it is on. Modelled with the mount at the origin and the arm reaching out
   * along +Z, so placing it needs a half turn against the fixture's own yaw,
   * which points away from the wall.
   */
  'prop.torch': { url: '/models/props/torch.glb', fit: 'none' },
  'level.bookshelf': { url: '/models/level/bookshelf.glb', fit: 'none' },
  'level.crate': { url: '/models/level/woodencrate.glb', fit: 'none' },

  /*
   * The cathedral's furniture.
   *
   * All `fit: 'none'`: these are real objects at real sizes, and stretching one
   * into a notional box would put a bench the size of the room it stands in.
   * Anything that is not 1:1 says so in `PROP_SCALE` rather than here, so that
   * the manifest stays a list of where things are.
   */
  'prop.bench': { url: '/models/props/bench.glb', fit: 'none' },
  'prop.cauldron': { url: '/models/props/cauldron.glb', fit: 'none' },
  'prop.workbench': { url: '/models/props/workbench.glb', fit: 'none' },
  'prop.crateMetal': { url: '/models/props/crate_metal.glb', fit: 'none' },
  'prop.crateFarm': { url: '/models/props/farmcrate_carrot.glb', fit: 'none' },
  'prop.arch': { url: '/models/props/wall_arch.glb', fit: 'none' },
  'prop.cage': { url: '/models/props/cage_small.glb', fit: 'none' },
  'prop.roofSupport': { url: '/models/props/roof_support2.glb', fit: 'none' },
  'prop.chain': { url: '/models/props/chain_coil.glb', fit: 'none' },

  /*
   * Wounding's axe, and everything else that throws a `mace`.
   *
   * A metre of bronze in the source; a quarter of a metre here, which is the
   * size a projectile wants. Its origin was recentred and its node transform
   * stripped offline, because a thrown object spins about its own origin and the
   * game aims it along its direction of travel — neither of which a manifest
   * can fix. `fit: 'none'` because it has a true size of its own, arrived at by
   * measurement rather than by being squeezed into a box.
   */
  'projectile.mace': {
    url: '/models/spells/axe.glb',
    // A metre of bronze in the source, a quarter of a metre in the air. This is
    // the *only* place its size is set: the effects draw a model at whatever
    // size the manifest gives it, where a procedural shape is a unit built to
    // `look.size`.
    scale: 0.256,
    fit: 'none',
    anchor: 'centre',
  },

  /*
   * All four share one skeleton and one clip library; what differs is the
   * geometry and the costume texture. Each was exported facing +Z against the
   * game's −Z, so each is turned here rather than in the model — one line
   * against a re-export.
   */
  'mage.magician': {
    url: '/models/mages/magician.glb',
    rotation: { y: Math.PI },
    animationsFrom: '/models/anim/mage.glb',
    clips: MAGE_CLIPS,
    clipSpeeds: MAGE_CLIP_SPEEDS,
    factionTextures: {
      MI_Wizard: {
        order: '/models/mages/tex/wizard-order.jpg',
        chaos: '/models/mages/tex/wizard-chaos.jpg',
        balance: '/models/mages/tex/wizard-balance.jpg',
      },
    },
  },

  'mage.cleric': {
    url: '/models/mages/cleric.glb',
    rotation: { y: Math.PI },
    animationsFrom: '/models/anim/mage.glb',
    clips: MAGE_CLIPS,
    clipSpeeds: MAGE_CLIP_SPEEDS,
    // Iron for Order, gold for Chaos, bronze for Balance. The armour is three
    // material slots in the export and one texture behind them, which the prefix
    // match handles from this single entry.
    factionTextures: {
      MI_Knight: {
        order: '/models/mages/tex/knight-order.jpg',
        chaos: '/models/mages/tex/knight-chaos.jpg',
        balance: '/models/mages/tex/knight-balance.jpg',
      },
    },
    // The scarf carries no texture of its own, so it takes a flat tint. Faction
    // reads twice over on this one: the armour and the cloth.
    tintMaterial: 'MW_Faction',
  },

  'mage.mentalist': {
    url: '/models/mages/mentalist.glb',
    rotation: { y: Math.PI },
    animationsFrom: '/models/anim/mage.glb',
    clips: MAGE_CLIPS,
    clipSpeeds: MAGE_CLIP_SPEEDS,
    factionTextures: {
      MI_Noble: {
        order: '/models/mages/tex/noble-order.jpg',
        chaos: '/models/mages/tex/noble-chaos.jpg',
        balance: '/models/mages/tex/noble-balance.jpg',
      },
    },
  },

  'mage.arcanist': {
    url: '/models/mages/arcanist.glb',
    rotation: { y: Math.PI },
    animationsFrom: '/models/anim/mage.glb',
    clips: MAGE_CLIPS,
    clipSpeeds: MAGE_CLIP_SPEEDS,
    // Two material slots behind one texture, both caught by the prefix.
    factionTextures: {
      MI_Ranger: {
        order: '/models/mages/tex/ranger-order.jpg',
        chaos: '/models/mages/tex/ranger-chaos.jpg',
        balance: '/models/mages/tex/ranger-balance.jpg',
      },
    },
  },
};
